/**
 * Grouped invoices — the rules behind the Invoice builder (views/reports/
 * InvoiceBuilder.tsx).
 *
 * Hourly work goes to a client as a few described lines for the month ("Board
 * retreat facilitation — 6.00 h"), not as every entry in the log. A draft holds
 * those lines; each line names the entries it covers, so the logged hours are
 * always one sum away and nothing is ever typed in twice.
 *
 * Rounding and hand-set hours live on the LINE, never on the entries. The log,
 * Reports, budgets and retainers keep reading what was actually worked; only
 * the invoice shows the billed figure, with the logged one beside it.
 *
 * Pure: imports types only, so tests/invoice-draft.test.mjs loads it directly.
 */

import type { InvoiceDraft, InvoiceLine, TimeEntry } from "./types";

type Draft = Pick<InvoiceDraft, "roundTo" | "roundMode">;
type ById = Map<string, TimeEntry>;

/** Increments offered for rounding a line, in hours. 0 is "no rounding". */
export const ROUND_STEPS: [number, string][] = [
  [0, "No rounding"], [0.1, "0.1 h (6 min)"], [0.25, "0.25 h (15 min)"], [0.5, "0.5 h"], [1, "1 h"],
];

/**
 * Hours snapped to an increment.
 *
 * Nearest by default; "up" never bills less than was worked. Either way a line
 * with any time on it bills at least one increment — the same floor the entry
 * rounding in lib/timeRollup applies, so ten minutes never prints as 0.00.
 */
export function roundHours(hours: number, step = 0, mode: "nearest" | "up" = "nearest"): number {
  const h = Math.max(0, hours);
  if (!(step > 0)) return Math.round(h * 100) / 100;
  // The epsilon absorbs float noise: 0.3 / 0.1 is 2.9999999999999996.
  const q = h / step;
  let n = mode === "up" ? Math.ceil(q - 1e-9) : Math.round(q + 1e-9);
  if (h > 0 && n === 0) n = 1;
  return Math.round(n * step * 10000) / 10000;
}

/** What was actually logged on the line, in seconds. */
export function loggedSeconds(line: InvoiceLine, byId: ById): number {
  return line.entryIds.reduce((n, id) => n + (byId.get(id)?.durationSeconds || 0), 0);
}

/** A hand-set figure wins; otherwise the logged hours, rounded. */
export function billedHours(line: InvoiceLine, draft: Draft, byId: ById): number {
  if (typeof line.hours === "number" && Number.isFinite(line.hours) && line.hours >= 0) return line.hours;
  return roundHours(loggedSeconds(line, byId) / 3600, draft.roundTo ?? 0, draft.roundMode ?? "nearest");
}

/** The one rate every entry on the line was logged at, or null when they differ. */
export function lineRate(line: InvoiceLine, byId: ById): number | null {
  const rates = new Set(line.entryIds.map((id) => byId.get(id)?.rateSnapshot ?? 0));
  if (rates.size !== 1) return null;
  const [r] = [...rates];
  return r > 0 ? r : null;
}

/**
 * The line's amount, to the cent.
 *
 * Each entry's own `rateSnapshot` prices its logged hours, then rounding scales
 * that sum by billed ÷ logged. With one rate that is just billed × rate; with
 * two (a rate change mid-month, or travel at a discount) the extra rounded time
 * is priced in the same mix as the work it rounds — and the printed rate is
 * left blank, as the project summary does, because no single rate is true.
 */
export function lineAmount(line: InvoiceLine, draft: Draft, byId: ById): number {
  const billed = billedHours(line, draft, byId);
  const logged = loggedSeconds(line, byId) / 3600;
  let amount: number;
  if (logged > 0) {
    const worth = line.entryIds.reduce((n, id) => {
      const e = byId.get(id);
      return n + ((e?.durationSeconds || 0) / 3600) * (e?.rateSnapshot ?? 0);
    }, 0);
    amount = worth * (billed / logged);
  } else {
    amount = billed * (lineRate(line, byId) ?? 0);
  }
  return Math.round(amount * 100) / 100;
}

export interface DraftTotals {
  loggedHours: number;
  billedHours: number;
  amount: number;
}

/** Sums of the lines as they print, so the total always equals its rows. */
export function draftTotals(lines: InvoiceLine[], draft: Draft, byId: ById): DraftTotals {
  let logged = 0, billed = 0, amount = 0;
  for (const l of lines) {
    logged += loggedSeconds(l, byId) / 3600;
    billed += billedHours(l, draft, byId);
    amount += lineAmount(l, draft, byId);
  }
  return {
    loggedHours: Math.round(logged * 100) / 100,
    billedHours: Math.round(billed * 10000) / 10000,
    amount: Math.round(amount * 100) / 100,
  };
}

/** "YYYY-MM" of the local day an entry was worked. */
export function entryMonth(e: Pick<TimeEntry, "start">): string {
  const d = new Date(e.start);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

/**
 * The entries a draft can draw on.
 *
 * Billable (hourly) time for its month and client — and project, if it has one
 * — that has not been invoiced. Entries already on THIS draft always count,
 * invoiced or not, so a sent invoice still reprints in full. An entry on any
 * other draft is held back, so the same hour can't be billed twice.
 */
export function draftPool(
  entries: TimeEntry[],
  draft: Pick<InvoiceDraft, "id" | "month" | "client" | "project" | "lines">,
  others: Pick<InvoiceDraft, "id" | "lines">[],
  isHourly: (e: TimeEntry) => boolean,
): { pool: TimeEntry[]; elsewhere: number } {
  const mine = new Set(draft.lines.flatMap((l) => l.entryIds));
  const taken = new Set(others.filter((o) => o.id !== draft.id).flatMap((o) => o.lines.flatMap((l) => l.entryIds)));
  let elsewhere = 0;
  const pool = entries.filter((e) => {
    if (mine.has(e.id)) return true;
    if (entryMonth(e) !== draft.month) return false;
    if (draft.client && e.client !== draft.client) return false;
    if (draft.project && e.project !== draft.project) return false;
    if (!isHourly(e) || e.invoicedAt) return false;
    if (taken.has(e.id)) { elsewhere++; return false; }
    return true;
  });
  return { pool: pool.sort((a, b) => (a.start || "").localeCompare(b.start || "")), elsewhere };
}

/** Pool entries not on any line yet — the "still to place" list. */
export function unassigned(pool: TimeEntry[], lines: InvoiceLine[]): TimeEntry[] {
  const used = new Set(lines.flatMap((l) => l.entryIds));
  return pool.filter((e) => !used.has(e.id));
}

/**
 * Drop entry ids that are no longer in the pool — deleted, or outside a month
 * or client the draft was changed to. Lines are kept even when emptied, since
 * their descriptions are work you may want back.
 */
export function reconcile(lines: InvoiceLine[], pool: TimeEntry[]): InvoiceLine[] {
  const ok = new Set(pool.map((e) => e.id));
  let changed = false;
  const next = lines.map((l) => {
    const ids = l.entryIds.filter((id) => ok.has(id));
    if (ids.length === l.entryIds.length) return l;
    changed = true;
    return { ...l, entryIds: ids };
  });
  return changed ? next : lines;
}

export const newLineId = (): string => `l${Math.random().toString(36).slice(2, 10)}`;

/**
 * Move entries onto a line (or a new one), taking them off wherever they were.
 * A new line is placed where its earliest entry falls, so the invoice reads in
 * the order the work happened.
 */
export function moveEntries(
  lines: InvoiceLine[],
  entryIds: string[],
  to: { lineId: string } | { description: string; id?: string },
): InvoiceLine[] {
  const moving = new Set(entryIds);
  const stripped = lines.map((l) => (l.entryIds.some((id) => moving.has(id))
    ? { ...l, entryIds: l.entryIds.filter((id) => !moving.has(id)) }
    : l));
  if ("lineId" in to) {
    return stripped.map((l) => (l.id === to.lineId ? { ...l, entryIds: [...l.entryIds, ...entryIds] } : l));
  }
  return [...stripped, { id: to.id ?? newLineId(), description: to.description, entryIds: [...entryIds] }];
}

export type GroupBy = "task" | "activity" | "entry";

/**
 * A first draft of the lines, so the work is merging and renaming rather than
 * building from nothing. By task: one line per Todoist task, plus one per
 * project for time logged to no task. By activity: one per activity. By entry:
 * every entry its own line, for a client who does want the detail.
 */
export function autoGroup(
  pool: TimeEntry[],
  by: GroupBy,
  names: { task: (id: string) => string | undefined; project: (id: string) => string | undefined; entry: (e: TimeEntry) => string },
  makeId: () => string = newLineId,
): InvoiceLine[] {
  const sorted = [...pool].sort((a, b) => (a.start || "").localeCompare(b.start || ""));
  if (by === "entry") return sorted.map((e) => ({ id: makeId(), description: names.entry(e), entryIds: [e.id] }));
  const groups = new Map<string, { description: string; ids: string[] }>();
  for (const e of sorted) {
    let key: string, description: string;
    if (by === "activity") {
      key = e.category || "";
      description = e.category || "General work";
    } else if (e.item && names.task(e.item)) {
      key = `t:${e.item}`;
      description = names.task(e.item)!;
    } else {
      key = `p:${e.project ?? ""}`;
      const p = e.project ? names.project(e.project) : undefined;
      description = p ? `${p} — general` : "General work";
    }
    const g = groups.get(key);
    if (g) g.ids.push(e.id);
    else groups.set(key, { description, ids: [e.id] });
  }
  return [...groups.values()].map((g) => ({ id: makeId(), description: g.description, entryIds: g.ids }));
}
